import {
  LINK_KIND_APPLICATION,
  LINK_KIND_CREDIT_AUTHORIZATION,
} from "../hooks/useApplicationLinksHook";

/**
 * The prefill PII boundary for borrower-facing links, mirroring the server's
 * PREFILL_ALLOWED / PREFILL_DENIED (lendmarq-os app/models/application_links.py).
 *
 * Kept here rather than inferred from the form because it is enforced twice, in
 * two directions:
 *   - on the way OUT, filtering what the send screen puts in a link, and
 *   - on the way IN, sanitizing what a public form accepts from a URL.
 *
 * The second one is the load-bearing case: a prefill-carrying URL is entirely
 * user-editable, so anything decoded from one has to be re-checked as if it were
 * hostile. Without that, a hand-crafted link could seed arbitrary fields into a
 * borrower's form.
 *
 * Nothing in DENIED is ever prefilled — the borrower types SSN and date of birth
 * themselves, and the server 400s on an attempt to include one.
 *
 * IMPORTANT: this list is what the server will ACCEPT, not what the public forms
 * actually ask. It was written against the legacy LQ application shape, and
 * several entries have no matching field in this portal's forms —
 * `middleName`, `nameSuffix`, `repeatBorrowerFlag`, `isExperienced` and
 * `experienceCount` are all absent (experience is collected per category as
 * `{less_than_three_years, greater_than_three_years}`, not as a single count).
 * Prefilling one of those is silently inert: it survives the allowlist, gets
 * stored on the link, and then lands on a field that does not exist.
 *
 * So do NOT read this list as a field spec for the send screen. Before offering
 * a prefill input, grep the target form for the field name.
 */
const ALLOWED_SHARED = [
  "firstName",
  "middleName",
  "lastName",
  "nameSuffix",
  "emailAddress",
  "phone",
  "entityName",
  "citizenshipStatus",
  "estimatedFICO",
  "repeatBorrowerFlag",
  "isExperienced",
  "experienceCount",
];

const ALLOWED_APPLICATION = [
  ...ALLOWED_SHARED,
  "loanProductType",
  "loanPurpose",
  "baseLoanAmount",
  "expectedClosingDate",
  // Allowed on an application, denied on a credit auth: name plus address is
  // most of an identity-theft payload.
  "primaryAddress",
  "subjectProperties",
];

export const PREFILL_DENIED = [
  "borrowerSSN",
  "ssn",
  "taxId",
  "ein",
  "dateOfBirth",
  "dob",
  "driversLicense",
  "passportNumber",
  "bankAccountNumber",
  "routingNumber",
  "creditScore",
  "creditReportId",
];

export const prefillAllowedFor = (linkKind) =>
  linkKind === LINK_KIND_APPLICATION ? ALLOWED_APPLICATION : ALLOWED_SHARED;

/**
 * Expected TYPE per field. An allowlist of keys is only half a boundary: a
 * `?p=` link is fully attacker-authored, so `{"firstName": {...}}` would sail
 * through a key-only check and reach a MUI input as an object, which React
 * throws on. Values are coerced to the declared type or dropped.
 */
const FIELD_TYPES = {
  firstName: "string",
  middleName: "string",
  lastName: "string",
  nameSuffix: "string",
  emailAddress: "string",
  phone: "string",
  entityName: "string",
  citizenshipStatus: "string",
  estimatedFICO: "string",
  repeatBorrowerFlag: "boolean",
  isExperienced: "boolean",
  experienceCount: "number",
  loanProductType: "string",
  loanPurpose: "string",
  baseLoanAmount: "number",
  expectedClosingDate: "string",
  primaryAddress: "address",
  subjectProperties: "properties",
};

// Bounds, not guesses at real data: long enough for any legitimate value, short
// enough that a crafted link cannot push megabytes into a form or the database.
const MAX_STRING = 200;
const MAX_ADDRESS_KEYS = 20;
const MAX_PROPERTIES = 10;

const cleanString = (value) => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, MAX_STRING) : undefined;
};

// A flat map of string fields — the shape RffAddressEditingField reads. Nested
// objects and arrays are dropped rather than walked: an address has no legitimate
// depth, so anything deeper is someone probing.
const cleanAddress = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const out = {};
  Object.entries(value)
    .filter(([key]) => !PREFILL_DENIED.includes(key))
    .slice(0, MAX_ADDRESS_KEYS)
    .forEach(([key, val]) => {
      const str = cleanString(val);
      if (str !== undefined) out[key] = str;
      else if (typeof val === "number" && Number.isFinite(val)) out[key] = val;
    });
  return Object.keys(out).length > 0 ? out : undefined;
};

// Only the `address` subobject of a property survives — no valuation, no budget.
const cleanProperties = (value) => {
  if (!Array.isArray(value)) return undefined;
  const out = value
    .slice(0, MAX_PROPERTIES)
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const address = cleanAddress(entry.address);
      return address ? { address } : null;
    })
    .filter(Boolean);
  return out.length > 0 ? out : undefined;
};

const coerceValue = (value, type) => {
  switch (type) {
    case "string":
      return cleanString(value);
    case "number": {
      const num = typeof value === "number" ? value : Number(value);
      return Number.isFinite(num) ? num : undefined;
    }
    case "boolean":
      return typeof value === "boolean" ? value : undefined;
    case "address":
      return cleanAddress(value);
    case "properties":
      return cleanProperties(value);
    default:
      return undefined;
  }
};

/**
 * Allowlist-first on the KEY, then type-coerced on the VALUE. Anything that
 * fails either check is dropped silently. Returns a plain object safe to hand
 * to react-final-form as initialValues.
 *
 * Note the iteration walks `allowed`, not `raw` — so the output key set is
 * bounded by our list no matter what the input contains, and a key like
 * `__proto__` can never reach the `clean[key] =` assignment.
 */
export const sanitizePrefill = (raw, linkKind) => {
  if (!raw || typeof raw !== "object") return {};
  const isCreditAuth = linkKind === LINK_KIND_CREDIT_AUTHORIZATION;
  const clean = {};

  prefillAllowedFor(linkKind).forEach((key) => {
    if (key === "primaryAddress" && isCreditAuth) return;
    if (!Object.prototype.hasOwnProperty.call(raw, key)) return;
    const value = coerceValue(raw[key], FIELD_TYPES[key]);
    if (value !== undefined) clean[key] = value;
  });

  return clean;
};
